/**
 * ============================================================================
 * MembershipInsights - Membership charts for Customer Insights
 * ============================================================================
 *
 * Exported subcomponents (used inline on the insights page):
 * - MetricCards — 4 KPI cards
 * - RetentionRing — Pie ring for retention %
 * - StatusDonut — Donut chart for status breakdown
 * - MemberTrendChart — Bar chart for new members + renewals
 * - TopMembersList — Top 5 members by spend
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Users, UserCheck, RefreshCw, Clock, Crown } from 'lucide-react';
import type {
  MembershipInsightsData,
  MembershipStatusBreakdown,
  MembershipTrendData,
  MembershipTopMember,
} from '../../types/customerInsights';

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(amount: number): string {
  if (amount >= 1000000) return `${CURRENCY_SYMBOL}${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${CURRENCY_SYMBOL}${(amount / 1000).toFixed(0)}K`;
  return `${CURRENCY_SYMBOL}${amount.toLocaleString()}`;
}

function formatDuration(days: number): string {
  if (days >= 30) {
    const months = Math.round(days / 30 * 10) / 10;
    return `${months} mo`;
  }
  return `${days}d`;
}

// ============================================================================
// EXPORTED SUBCOMPONENTS
// ============================================================================

export function MetricCards({ metrics }: { metrics: MembershipInsightsData['metrics'] }) {
  const cards = [
    {
      label: 'Total Members',
      value: metrics.total_members.toString(),
      icon: Users,
      colorClass: 'bg-blue-50 text-blue-600',
    },
    {
      label: 'Active Members',
      value: metrics.active_members.toString(),
      icon: UserCheck,
      colorClass: 'bg-green-50 text-green-600',
    },
    {
      label: 'Retention Rate',
      value: `${metrics.retention_rate}%`,
      icon: RefreshCw,
      colorClass: 'bg-purple-50 text-purple-600',
    },
    {
      label: 'Avg Duration',
      value: formatDuration(metrics.avg_duration_days),
      icon: Clock,
      colorClass: 'bg-orange-50 text-orange-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {cards.map((card, idx) => (
        <div key={idx} className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">{card.label}</p>
              <p className="text-lg sm:text-xl font-black text-gray-900">{card.value}</p>
            </div>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${card.colorClass} flex items-center justify-center flex-shrink-0 ml-2`}>
              <card.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RetentionRing({ rate }: { rate: number }) {
  const ringData = [
    { name: 'Retained', value: rate, color: '#8b5cf6' },
    { name: 'Churned', value: 100 - rate, color: '#e5e7eb' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Retention Rate</h3>
      <p className="text-xs sm:text-sm text-gray-500 mb-4">Members who renewed after expiry</p>

      <div className="flex items-center justify-center">
        <div className="relative" style={{ width: 180, height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={ringData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                startAngle={90}
                endAngle={-270}
                paddingAngle={0}
                dataKey="value"
                stroke="none"
              >
                {ringData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-black text-gray-900">{rate}%</span>
            <span className="text-xs text-gray-500">retained</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 mt-4">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
          <span className="text-xs text-gray-600">Renewed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200"></div>
          <span className="text-xs text-gray-600">Churned</span>
        </div>
      </div>
    </div>
  );
}

export function StatusDonut({ data }: { data: MembershipStatusBreakdown[] }) {
  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Member Status</h3>
      <p className="text-xs sm:text-sm text-gray-500 mb-4">Current membership status breakdown</p>

      <div className="flex items-center justify-center">
        <div className="relative" style={{ width: 180, height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '8px 12px',
                  fontSize: '13px',
                }}
                formatter={(value) => [`${value} members`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-black text-gray-900">{total}</span>
            <span className="text-xs text-gray-500">total</span>
          </div>
        </div>
      </div>

      <div className="space-y-2.5 mt-4">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm text-gray-700 font-medium">{item.name}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-semibold text-gray-900">{item.count}</span>
              <span className="text-xs text-gray-400 ml-1">({item.value}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MemberTrendChart({ data }: { data: MembershipTrendData[] }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Member Trend</h3>
          <p className="text-xs sm:text-sm text-gray-500">New signups and renewals over time</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
            <span className="text-xs text-gray-600">New</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-gray-600">Renewals</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '8px 12px',
              fontSize: '13px',
            }}
          />
          <Bar dataKey="new_members" name="New Members" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="renewals" name="Renewals" fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TopMembersList({ members }: { members: MembershipTopMember[] }) {
  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    expired: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
    none: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Members</h3>
          <p className="text-xs sm:text-sm text-gray-500">By total membership spend</p>
        </div>
        <Crown className="w-5 h-5 text-amber-500" />
      </div>

      <div className="space-y-0">
        {members.map((member, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
          >
            {/* Left: Rank + Name */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs sm:text-sm font-bold text-gray-500">{idx + 1}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColors[member.current_status]}`}>
                    {member.current_status}
                  </span>
                  {member.current_package && (
                    <span className="text-[10px] sm:text-xs text-gray-400 truncate">{member.current_package}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Spend + Count */}
            <div className="text-right flex-shrink-0 ml-2">
              <p className="text-sm font-bold text-gray-900">{formatCurrency(member.total_spent)}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">{member.total_memberships} membership{member.total_memberships > 1 ? 's' : ''}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
